import React, { FC } from 'react';
import { Button, Col, Row } from 'antd';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { faGaugeHigh, Icon, PageContainer, Title } from '@/ui/components';
import { useTranslate } from '@/translations';
import { KpiSummaryRow } from './components/KpiSummaryRow';
import { ThroughputTrendChart } from './components/ThroughputTrendChart';
import { StockByModelChart } from './components/StockByModelChart';
import { StockByStatusChart } from './components/StockByStatusChart';
import { StockByCompoundChart } from './components/StockByCompoundChart';
import { LoadPullingOverview } from './components/LoadPullingOverview';
import { MaintenanceTable } from './components/MaintenanceTable';
import { CompoundHeatmap } from './components/CompoundHeatmap';
import { TrackingMap } from './components/TrackingMap';

const rowStyle = { marginBottom: 16 };

const formatWeekLabel = (): string => {
  const now = new Date();
  const dayOffset = (now.getDay() + 6) % 7;
  const monday = new Date(now);
  monday.setDate(now.getDate() - dayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const iso = new Date(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()),
  );
  iso.setUTCDate(iso.getUTCDate() - ((iso.getUTCDay() + 6) % 7) + 3);
  const firstThursday = new Date(Date.UTC(iso.getUTCFullYear(), 0, 4));
  const week =
    1 +
    Math.round(
      ((iso.getTime() - firstThursday.getTime()) / 86400000 -
        3 +
        ((firstThursday.getUTCDay() + 6) % 7)) /
        7,
    );

  const fmt = (dt: Date) =>
    dt.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  return `Week ${week} | ${fmt(monday)} - ${fmt(sunday)}`;
};

const isDashboardQuery = (query: { queryKey: readonly unknown[] }) =>
  typeof query.queryKey[0] === 'string' &&
  (query.queryKey[0] as string).startsWith('dashboard');

export const OperationsDashboard: FC = () => {
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const isRefreshing = useIsFetching({ predicate: isDashboardQuery }) > 0;

  const handleRefresh = () =>
    queryClient.invalidateQueries({ predicate: isDashboardQuery });

  return (
    <PageContainer>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
          <Title title={t('dashboard.title')} icon={faGaugeHigh} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span
            style={{
              background: '#eef1f7',
              color: '#5b6b8c',
              fontSize: 13,
              fontWeight: 500,
              padding: '5px 14px',
              borderRadius: 16,
              whiteSpace: 'nowrap',
            }}
          >
            {formatWeekLabel()}
          </span>
          <Button
            icon={<Icon icon={faArrowsRotate} />}
            loading={isRefreshing}
            onClick={handleRefresh}
          >
            {t('dashboard.refresh')}
          </Button>
        </div>
      </div>

      <KpiSummaryRow />

      <Row gutter={[16, 16]} style={rowStyle}>
        <Col span={24}>
          <ThroughputTrendChart />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={rowStyle}>
        <Col lg={12} xs={24}>
          <StockByModelChart />
        </Col>
        <Col lg={12} xs={24}>
          <StockByStatusChart />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={rowStyle}>
        <Col lg={12} xs={24}>
          <StockByCompoundChart />
        </Col>
        <Col lg={12} xs={24}>
          <LoadPullingOverview />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={rowStyle}>
        <Col lg={12} xs={24}>
          <MaintenanceTable />
        </Col>
        <Col lg={12} xs={24}>
          <CompoundHeatmap />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={rowStyle}>
        <Col span={24}>
          <TrackingMap />
        </Col>
      </Row>
    </PageContainer>
  );
};
